<template>
    <div class="search-box relative z-50 w-full" v-click-outside="handleClickOutside">
      
      <!-- SEARCH INPUT WRAPPER -->
      <div class="header-search-field group relative flex h-12 w-full items-center rounded-full border border-slate-200/60 bg-white/80 pr-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.45),0_2px_6px_rgba(15,23,42,0.06)] backdrop-blur-sm transition-all duration-200 focus-within:border-brand focus-within:bg-white">
          <PhMagnifyingGlass
              :size="20"
              weight="bold"
              class="pointer-events-none absolute left-4 shrink-0 text-[#888888] transition-colors group-focus-within:text-brand"
              aria-hidden="true"
          />

          <input
              ref="searchInput"
              v-model="searchData"
              type="text"
              placeholder="Поиск товаров (например: лопата)"
              class="min-w-0 flex-1 border-0 bg-transparent py-0 pl-11 pr-2 text-sm font-medium text-gray-900 shadow-none outline-none ring-0 placeholder:text-[#888888] focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
              aria-label="Поиск товаров"
              @keydown.enter="goSearch()"
              @focus="isFocused = true"
          >

          <button
              v-if="searchData.length > 0 && !isLoading"
              type="button"
              class="mr-1 shrink-0 text-[#888888] transition-colors hover:text-slate-700"
              title="Очистить"
              @click="resetSearch"
          >
              <PhXCircle :size="18" weight="fill" />
          </button>

          <PhSpinner v-if="isLoading" :size="18" class="mr-2 shrink-0 animate-spin text-brand" />

          <button
              type="button"
              class="inline-flex h-9 shrink-0 items-center justify-center rounded-full bg-brand px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-accent disabled:opacity-70"
              :disabled="isLoading"
              @click="goSearch"
          >
              Найти
          </button>
      </div>
  
      <!-- DROPDOWN RESULTS -->
      <transition name="dropdown">
          <div 
              v-if="shouldShowDropdown" 
              class="absolute top-full left-0 w-full mt-2 bg-white rounded-[24px] shadow-2xl shadow-gray-900/10 border border-gray-100 overflow-hidden flex flex-col max-h-[80vh]"
          >
              
              <!-- SCROLLABLE CONTENT -->
              <div class="overflow-y-auto p-1.5 custom-scrollbar">
                  
                  <!-- No Results -->
                  <div v-if="!hasSearchResults && !isLoading" class="p-8 text-center text-gray-400">
                      <p class="text-sm font-medium">По запросу «{{ searchData }}» ничего не найдено</p>
                  </div>
  
                  <!-- 1. CATEGORIES SECTION -->
                  <div v-if="searchCategories.size > 0" class="mb-1.5">
                      <div class="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                          <PhStorefront :size="14" weight="fill" />
                          Категории
                      </div>
                      <div class="space-y-0">
                          <router-link 
                              v-for="[slug, title] in searchCategories" 
                              :key="slug" 
                              :to="`/category/${slug}`"
                              @click="clearSearchResults"
                              class="flex items-center gap-3 px-4 py-2 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-[#DC2626] transition-colors group"
                          >
                              <div class="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                                  <PhStorefront :size="20" weight="fill" class="text-gray-400 group-hover:text-[#DC2626] transition-colors" />
                              </div>
                              <span v-typo class="flex-1 min-w-0 line-clamp-1">{{ title }}</span>
                              <PhCaretRight :size="14" weight="bold" class="text-gray-300 group-hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-all shrink-0" />
                          </router-link>
                      </div>
                  </div>
  
                  <div v-if="searchCategories.size > 0 && searchResult.length > 0" class="h-px bg-gray-100 my-1.5 mx-4"></div>
  
                  <!-- 2. PRODUCTS SECTION -->
                  <div v-if="searchResult.length > 0">
                      <div class="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                          <PhPackage :size="14" weight="fill" />
                          Товары
                      </div>
                      <div class="space-y-0">
                          <router-link 
                              v-for="item in searchResult" 
                              :key="getProductKey(item)" 
                              :to="getProductRoute(item)"
                              @click="clearSearchResults"
                              class="flex items-center gap-3 px-4 py-1.5 rounded-full hover:bg-gray-50 group transition-colors"
                          >
                              <div class="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                                  <img 
                                      v-if="getProductImageUrl(item)" 
                                      :src="getProductImageUrl(item)" 
                                      :alt="getProductName(item)"
                                      class="w-full h-full object-cover"
                                      loading="lazy"
                                      @error="handleImageError($event, item)"
                                  />
                                  <PhPackage v-else :size="20" weight="fill" class="text-gray-400 group-hover:text-[#DC2626] transition-colors" />
                              </div>
                              <span 
                                  class="text-sm text-gray-700 group-hover:text-gray-900 line-clamp-1 flex-1 min-w-0"
                                  v-html="highlightMatch(getProductName(item))"
                              ></span>
                          </router-link>
                      </div>
                  </div>
  
              </div>
  
              <!-- FOOTER: View All -->
              <div v-if="hasSearchResults" class="p-2 border-t border-gray-100 bg-gray-50/50">
                  <button 
                      @click="goSearch" 
                      class="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-white border border-gray-200 text-sm font-bold text-gray-900 hover:border-[#DC2626] hover:text-[#DC2626] transition-all shadow-sm"
                  >
                      Показать все результаты
                      <PhArrowRight :size="16" weight="bold" />
                  </button>
              </div>
          </div>
      </transition>
    </div>
  </template>
  
  <script>
  import gql from 'graphql-tag'
  import { 
      PhMagnifyingGlass, 
      PhSpinner, 
      PhXCircle, 
      PhStorefront, 
      PhPackage, 
      PhCaretRight,
      PhArrowRight
  } from '@phosphor-icons/vue'
  import { MIN_SEARCH_QUERY_LENGTH, HEADER_SEARCH_RESULTS_COUNT, ROUTES, SORT_ORDERS } from '@/constants'
  import { typo } from '@/utils/typography'
  
  export default {
      name: 'HeaderSearch',
      components: {
          PhMagnifyingGlass,
          PhSpinner,
          PhXCircle,
          PhStorefront,
          PhPackage,
          PhCaretRight,
          PhArrowRight
      },
      data() {
          return {
              searchData: '',
              searchResult: [],
              isFocused: false,
              isLoading: false,
              debounceTimeout: null,
              imageErrorIds: [], // id товаров, у которых не загрузилась миниатюра
          }
      },
      // Директива для клика вне компонента (если не подключена глобально)
      directives: {
          'click-outside': {
              mounted(el, binding) {
                  el.clickOutsideEvent = function(event) {
                      if (!(el === event.target || el.contains(event.target))) {
                          binding.value(event, el);
                      }
                  };
                  document.body.addEventListener('click', el.clickOutsideEvent);
              },
              unmounted(el) {
                  document.body.removeEventListener('click', el.clickOutsideEvent);
              },
          },
      },
      computed: {
          shouldShowDropdown() {
              // Показываем дропдаун только если есть фокус, есть текст и (есть результаты ИЛИ идет загрузка)
              // Но обычно лоадер лучше показывать внутри инпута, а дропдаун только для результатов
              return this.isFocused && this.searchData.length >= MIN_SEARCH_QUERY_LENGTH && (this.hasSearchResults || (!this.isLoading && this.searchData.length > 0 && !this.hasSearchResults && this.searchResult !== false));
          },
          hasSearchResults() {
              return this.searchResult && this.searchResult.length > 0
          },
          searchCategories() {
              const cats = new Map()
              if (!this.hasSearchResults) return cats
              
              for (const node of this.searchResult) {
                  const category = node?.node?.category
                  if (category?.slug && !cats.has(category.slug)) {
                      cats.set(category.slug, category.title)
                  }
              }
              return cats
          }
      },
      watch: {
          searchData(newVal) {
              // Сбрасываем старый таймер
              if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
  
              if (!newVal || newVal.length < MIN_SEARCH_QUERY_LENGTH) {
                  this.searchResult = [];
                  return;
              }
  
              this.isLoading = true; // Сразу показываем спиннер
  
              // Ставим новый таймер (300мс задержка)
              this.debounceTimeout = setTimeout(() => {
                  this.doSearch();
              }, 400); 
          }
      },
      methods: {
          handleClickOutside() {
              this.isFocused = false;
          },
          goSearch() {
              const trimmedQuery = this.searchData?.trim()
              if (trimmedQuery) {
                  this.$router.push(`/search/${encodeURIComponent(trimmedQuery)}`)
                  this.isFocused = false
                  this.$refs.searchInput.blur()
              }
          },
          resetSearch() {
              this.searchData = ''
              this.searchResult = []
              this.imageErrorIds = []
              this.$refs.searchInput.focus()
          },
          clearSearchResults() {
              this.isFocused = false
          },
          // Подсветка совпадений жирным
          highlightMatch(text) {
              if (!text) return text;
              const formatted = typo(text);
              if (!this.searchData) return formatted;
              const escaped = this.searchData.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const regex = new RegExp(`(${escaped})`, 'gi');
              return formatted.replace(regex, '<b class="text-[#DC2626]">$1</b>');
          },
          // --- Helpers ---
          getProductRoute(item) {
              if (!item?.node) return ROUTES.CATALOG
              const categorySlug = item.node.category?.slug
              const productSlug = item.node.slug
              if (!categorySlug || !productSlug) return ROUTES.CATALOG
              return `/category/${categorySlug}/product/${productSlug}`
          },
          getProductName(item) {
              return item?.node?.name || '';
          },
          getProductKey(item) {
              return item?.node?.id || `prod-${Math.random()}`
          },
          getProductImageUrl(item) {
              const id = item?.node?.id
              if (id && this.imageErrorIds.includes(id)) return null
              return item?.node?.mainImage?.url || null
          },
          handleImageError(event, item) {
              const id = item?.node?.id
              if (id && !this.imageErrorIds.includes(id)) {
                  this.imageErrorIds.push(id)
              }
          },
          // --- API ---
          doSearch() {
              const searchQuery = this.searchData?.trim()
              
              this.$apollo.query({
                  query: gql`
                      query HeaderSearch($searchQuery: String!, $sortOrder: ProductSortOrder!, $first: Int!) {
                          products(first: $first, sortOrder: $sortOrder, searchQuery: $searchQuery) {
                              edges {
                                  node {
                                      id
                                      name
                                      slug
                                      mainImage { url }
                                      category {
                                          slug
                                          title
                                      }
                                  }
                              }
                          }
                      }
                  `,
                  variables: {
                      searchQuery: searchQuery,
                      sortOrder: SORT_ORDERS.CHEAPEST_FIRST,
                      first: HEADER_SEARCH_RESULTS_COUNT
                  },
                  fetchPolicy: 'no-cache'
              })
              .then((response) => {
                  this.imageErrorIds = []
                  this.searchResult = response?.data?.products?.edges || []
              })
              .catch(() => {
                  this.searchResult = []
              })
              .finally(() => {
                  this.isLoading = false
              })
          }
      }
  }
  </script>
  
  <style scoped>
  /* Плавное появление дропдауна */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: all 0.2s ease-out;
  }
  
  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
  
  /* Кастомный скроллбар */
  .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: #e5e7eb;
      border-radius: 20px;
  }
  </style>